import { Injectable } from "@angular/core";
import { Feature, FeatureCollection, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from "geojson";
import { ReplaySubject } from 'rxjs/ReplaySubject';
import proj4 from "proj4";

import find from 'lodash/find';

@Injectable()
export class GisService {

    public static projections$ = new ReplaySubject();

    private static _projections = {
        "EPSG:102067": {
            id: "EPSG:102067",
            def:
                "+title=Krovak +proj=krovak +lat_0=49.5 +lon_0=42.5 +alpha=30.28813972222222 +k=0.9999 +x_0=0 +y_0=0 +ellps=bessel +pm=ferro +units=m +towgs84=570.8,85.7,462.8,4.998,1.587,5.261,3.56 +czech +no_defs"
        },
        "EPSG:102757": {
            id: "EPSG:102757",
            def:
                "+title=NAD 1983 StatePlane Wyoming West Central FIPS 4903 Feet +proj=tmerc +lat_0=40.5 +lon_0=-108.75 +x_0=600000.0 +y_0=0 +k=0.999938 +a=6378137.0  +b=6356752.3141403 +to_meter=0.3048006096012192"
        },
        "EPSG:102758": {
            id: "EPSG:102758",
            def:
                "+title=NAD 1983 StatePlane Wyoming West FIPS 4904 Feet +proj=tmerc +lat_0=40.5  +lon_0=-110.0833333333333 +x_0=800000  +y_0=100000  +k=0.999938 +a=6378137.0 +b=6356752.3141403 +to_meter=0.3048006096012192"
        },
        "EPSG:2154": {
            id: "EPSG:2154",
            def:
                "+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 99415.2, lat: 233683.27 },
                northEast: { lng: 669342.5, lat: 981936.72 }
            }
        },
        "EPSG:21781": {
            id: "EPSG:21781",
            def:
                "+title=CH1903 / LV03 +proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +x_0=600000 +y_0=200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs"
        },
        "EPSG:2169": {
            id: "EPSG:2169",
            def:
                "+proj=tmerc +lat_0=49.83333333333334 +lon_0=6.166666666666667 +k=1 +x_0=80000 +y_0=100000 +ellps=intl +towgs84=-189.681,18.3463,-42.7695,-0.33746,-3.09264,2.53861,0.4598 +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 48250.9349, lat: 60645.7231 },
                northEast: { lng: 106224.1264, lat: 141869.5837 }
            }
        },
        "EPSG:2192": {
            id: "EPSG:2192",
            def:
                "+proj=lcc +lat_1=46.8 +lat_0=46.8 +lon_0=2.337229166666667 +k_0=0.99987742 +x_0=600000 +y_0=2200000 +ellps=intl +units=m +no_defs",
            maxBounds: {
                southWest: { lng: -22918.6681, lat: 1712194.7333 },
                northEast: { lng: 1087301.8616, lat: 2703979.5939 }
            }
        },
        "EPSG:26591": {
            id: "EPSG:26591",
            def:
                "+title= Monte Mario (Rome) / Italy zone 1 EPSG:26591 +proj=tmerc +lat_0=0 +lon_0=-3.45233333333333 +from_greenwich=12.45233333333333 +k=0.999600 +x_0=1500000 +y_0=0 +a=6378388.0, +b=6356911.94612795 +units=m"
        },
        "EPSG:26912": {
            id: "EPSG:26912",
            def:
                "+title=NAD83 / UTM zone 12N +proj=utm +zone=12 +a=6378137.0 +b=6356752.3141403"
        },
        "EPSG:27200": {
            id: "EPSG:27200",
            def:
                "+title=New Zealand Map Grid  +proj=nzmg +lat_0=-41 +lon_0=173 +x_0=2510000 +y_0=6023150 +ellps=intl +datum=nzgd49 +units=m +no_defs"
        },
        "EPSG:27561": {
            id: "EPSG:27561",
            def:
                "+proj=lcc +lat_1=49.50000000000001 +lat_0=49.50000000000001 +lon_0=0 +k_0=0.999877341 +x_0=600000 +y_0=200000 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 40063.0848, lat: 67022.9279 },
                northEast: { lng: 1038149.3679, lat: 404377.9733 }
            }
        },
        "EPSG:27562": {
            id: "EPSG:27562",
            def:
                "+proj=lcc +lat_1=46.8 +lat_0=46.8 +lon_0=0 +k_0=0.99987742 +x_0=600000 +y_0=200000 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 50198.0586, lat: 64100.6841 },
                northEast: { lng: 1016966.9382, lat: 373525.1009 }
            }
        },
        "EPSG:27563": {
            id: "EPSG:27563",
            def:
                "+title=NTF (Paris)/Lambert Sud France +proj=lcc +lat_1=44.10000000000001 +lat_0=44.10000000000001 +lon_0=0 +k_0=0.9998774990000001 +x_0=600000 +y_0=200000 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 262373.4731, lat: 11755.1415 },
                northEast: { lng: 1047663.1958, lat: 364038.4062 }
            }
        },
        "EPSG:27564": {
            id: "EPSG:27564",
            def:
                "+proj=lcc +lat_1=42.16500000000001 +lat_0=42.16500000000001 +lon_0=0 +k_0=0.99994471 +x_0=234.358 +y_0=185861.369 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 501997.4013, lat: 111734.6001 },
                northEast: { lng: 607585.0937, lat: 309328.6374 }
            }
        },
        "EPSG:27571": {
            id: "EPSG:27571",
            def:
                "+proj=lcc +lat_1=49.50000000000001 +lat_0=49.50000000000001 +lon_0=0 +k_0=0.999877341 +x_0=600000 +y_0=1200000 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 40063.0848, lat: 1067022.9279 },
                northEast: { lng: 1038149.3679, lat: 1404377.9733 }
            }
        },
        "EPSG:27572": {
            id: "EPSG:27572",
            def:
                "+proj=lcc +lat_1=46.8 +lat_0=46.8 +lon_0=0 +k_0=0.99987742 +x_0=600000 +y_0=2200000 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: -22841.3905, lat: 1712212.6192 },
                northEast: { lng: 1087335.9605, lat: 2703971.1254 }
            }
        },
        "EPSG:27573": {
            id: "EPSG:27573",
            def:
                "+proj=lcc +lat_1=44.10000000000001 +lat_0=44.10000000000001 +lon_0=0 +k_0=0.999877499 +x_0=600000 +y_0=3200000 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 262373.4731, lat: 3011755.1415 },
                northEast: { lng: 1047663.1958, lat: 3364038.4062 }
            }
        },
        "EPSG:27574": {
            id: "EPSG:27574",
            def:
                "+proj=lcc +lat_1=42.16500000000001 +lat_0=42.16500000000001 +lon_0=0 +k_0=0.99994471 +x_0=234.358 +y_0=4185861.369 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 501997.4013, lat: 4111734.6001 },
                northEast: { lng: 607585.0937, lat: 4309328.6374 }
            }
        },
        "EPSG:32630": {
            id: "EPSG:32630",
            def:
                "+proj=utm +zone=30 +ellps=WGS84 +datum=WGS84 +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 166021.4431, lat: 0.0 },
                northEast: { lng: 833978.5569, lat: 9329005.1825 }
            }
        },
        "EPSG:32631": {
            id: "EPSG:32631",
            def:
                "+proj=utm +zone=31 +ellps=WGS84 +datum=WGS84 +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 166021.4431, lat: 0.0 },
                northEast: { lng: 833978.5569, lat: 9329005.1825 }
            }
        },
        "EPSG:32632": {
            id: "EPSG:32632",
            def:
                "+proj=utm +zone=32 +ellps=WGS84 +datum=WGS84 +units=m +no_defs",
            maxBounds: {
                southWest: { lng: 166021.4431, lat: 0.0 },
                northEast: { lng: 833978.5569, lat: 9329005.1825 }
            }
        },
        "EPSG:3942": {
            id: "EPSG:3942",
            def:
                "+proj=lcc +lat_1=41.25 +lat_2=42.75 +lat_0=42 +lon_0=3 +x_0=1700000 +y_0=1200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 1100257.6521, lng: 1296335.9393 },
                northEast: { lat: 1331811.4895, lng: 2254779.1544 }
            }
        },
        "EPSG:3943": {
            id: "EPSG:3943",
            def:
                "+proj=lcc +lat_1=42.25 +lat_2=43.75 +lat_0=43 +lon_0=3 +x_0=1700000 +y_0=2200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 2100044.3184, lng: 1302506.4384 },
                northEast: { lat: 2322094.8666, lng: 2093357.4402 }
            }
        },
        "EPSG:3944": {
            id: "EPSG:3944",
            def:
                "+proj=lcc +lat_1=43.25 +lat_2=44.75 +lat_0=44 +lon_0=3 +x_0=1700000 +y_0=3200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 3100052.0968, lng: 1308798.423 },
                northEast: { lat: 3322128.5732, lng: 2087131.0906 }
            }
        },
        "EPSG:3945": {
            id: "EPSG:3945",
            def:
                "+proj=lcc +lat_1=44.25 +lat_2=45.75 +lat_0=45 +lon_0=3 +x_0=1700000 +y_0=4200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 4098245.7319, lng: 1351247.6083 },
                northEast: { lat: 4321920.5102, lng: 2080786.3812 }
            }
        },
        "EPSG:3946": {
            id: "EPSG:3946",
            def:
                "+proj=lcc +lat_1=45.25 +lat_2=46.75 +lat_0=46 +lon_0=3 +x_0=1700000 +y_0=5200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 5097385.5589, lng: 1270591.648 },
                northEast: { lat: 5325334.0729, lng: 2027087.9253 }
            }
        },
        "RGF93_CC47": {
            id: "RGF93_CC47",
            epsg: "EPSG:3947",
            label: "RGF93 / Lambert-93",
            def:
                'PROJCS["RGF93_CC47",GEOGCS["GCS_RGF93",DATUM["D_RGF_1993",SPHEROID["GRS_1980",6378137,298.257222101]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]],PROJECTION["Lambert_Conformal_Conic"],PARAMETER["standard_parallel_1",46.25],PARAMETER["standard_parallel_2",47.75],PARAMETER["latitude_of_origin",47],PARAMETER["central_meridian",3],PARAMETER["false_easting",1700000],PARAMETER["false_northing",6200000],UNIT["Meter",1]]',
            maxBounds: {
                southWest: { lat: 6100231.0759, lng: 1112163.1498 },
                northEast: { lat: 6338667.6413, lng: 2071615.7622 }
            }
        },
        "EPSG:3947": {
            id: "EPSG:3947",
            def:
                "+proj=lcc +lat_1=46.25 +lat_2=47.75 +lat_0=47 +lon_0=3 +x_0=1700000 +y_0=6200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 6100231.0759, lng: 1112163.1498 },
                northEast: { lat: 6338667.6413, lng: 2071615.7622 }
            }
        },
        "EPSG:3948": {
            id: "EPSG:3948",
            def:
                "+proj=lcc +lat_1=47.25 +lat_2=48.75 +lat_0=48 +lon_0=3 +x_0=1700000 +y_0=7200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 7102517.8254, lng: 1092603.2215 },
                northEast: { lat: 7341541.8099, lng: 2100529.4893 }
            }
        },
        "EPSG:3949": {
            id: "EPSG:3949",
            def:
                "+proj=lcc +lat_1=48.25 +lat_2=49.75 +lat_0=49 +lon_0=3 +x_0=1700000 +y_0=8200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 8102449.7137, lng: 1104062.1794 },
                northEast: { lat: 8341410.9019, lng: 2092985.7591 }
            }
        },
        "EPSG:3950": {
            id: "EPSG:3950",
            def:
                "+proj=lcc +lat_1=49.25 +lat_2=50.75 +lat_0=50 +lon_0=3 +x_0=1700000 +y_0=9200000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 9100766.7009, lng: 1338040.0982 },
                northEast: { lat: 9324263.6899, lng: 2085321.7705 }
            }
        },
        "EPSG:41001": {
            id: "EPSG:41001",
            def:
                "+title=simple mercator EPSG:41001 +proj=merc +lat_ts=0 +lon_0=0 +k=1.000000 +x_0=0 +y_0=0 +ellps=WGS84 +datum=WGS84 +units=m"
        },
        "EPSG:4139": {
            id: "EPSG:4139",
            def:
                "+title=Puerto Rico EPSG:4139 (3 param datum shift) +proj=longlat +towgs84 = 11,72,-101,0,0,0,0 +a=6378206.4 +b=6356583.8"
        },
        "EPSG:4171": {
            id: "EPSG:4171",
            def:
                "+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs",
            maxBounds: {
                southWest: { lat: -9.62, lng: 41.18 },
                northEast: { lat: 10.3, lng: 51.54 }
            }
        },
        "EPSG:4181": {
            id: "EPSG:4181",
            def:
                "+title=Luxembourg 1930 EPSG:4181 (7 param datum shift) +proj=longlat +towgs84=-193,13.7,-39.3,-0.41,-2.933,2.688,0.43 +a=6378388.0, +b=6356911.94612795"
        },
        "EPSG:42304": {
            id: "EPSG:42304",
            def:
                "+title=Atlas of Canada, LCC +proj=lcc +lat_1=49 +lat_2=77 +lat_0=49 +lon_0=-95 +x_0=0 +y_0=0 +ellps=GRS80 +datum=NAD83 +units=m +no_defs"
        },
        "EPSG:4272": {
            id: "EPSG:4272",
            def:
                "+title=NZGD49 +proj=longlat +ellps=intl +datum=nzgd49 +no_defs "
        },
        "EPSG:4302": {
            id: "EPSG:4302",
            def:
                "+title=Trinidad 1903 EPSG:4302 (7 param datum shift) +proj=longlat +a=6378293.63683822 +b=6356617.979337744 +towgs84=-61.702,284.488,472.052,0,0,0,0"
        },
        "EPSG:4326": {
            id: "EPSG:4326",
            def: "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs",
            maxBounds: {
                southWest: { lat: -90, lng: -180 },
                northEast: { lat: 90, lng: 180 }
            }
        },
        "EPSG:4807": {
            id: "EPSG:4807",
            def:
                "+proj=longlat +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +no_defs"
        },
        "EPSG:900913": {
            id: "EPSG:900913",
            def:
                "+title=GoogleMercator +proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +no_defs",
            maxBounds: {
                southWest: { lat: -85, lng: -180 },
                northEast: { lat: 85, lng: 180 }
            }
        },
        "EPSG:3857": {
            id: "EPSG:3857",
            def:
                "+title=WebMercator +proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +a=6378137 +b=6378137 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs ",
            maxBounds: {
                southWest: { lat: -85, lng: -180 },
                northEast: { lat: 85, lng: 180 }
            }
        },
        "EPSG:27592": {
            id: "EPSG:27592",
            def:
                "+proj=lcc +lat_1=46.8 +lat_0=46.8 +lon_0=0 +k_0=0.99987742 +x_0=600000 +y_0=200000 +a=6378249.2 +b=6356515 +towgs84=-168,-60,320,0,0,0,0 +pm=paris +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 64100.6841, lng: 50198.0586 },
                northEast: { lat: 373525.1009, lng: 1016966.9382 }
            }
        },
        "EPSG:2972": {
            id: "EPSG:2972",
            def:
                "+proj=utm +zone=22 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
            maxBounds: {
                southWest: { lat: 64100.6841, lng: 50198.0586 },
                northEast: { lat: 373525.1009, lng: 1016966.9382 }
            }
        }
    };

    public static get PROJECTIONS(): { [key: string]: any } {
        return GisService._projections;
    }
    
    public static setProjectionLabels(projections: Array<{'epsg': string, 'name': string}>) {
        for(let projection of projections) {
            if (GisService.PROJECTIONS["EPSG:" + projection['epsg']])
                GisService.PROJECTIONS["EPSG:" + projection['epsg']]['label'] = projection['name'];
        }
        this.projections$.next(GisService.PROJECTIONS);
    }

    public static reprojectGeometryCollection(
        fromP: string,
        toP: string,
        geometry: any
    ): any {
        let reprojGeoms = [];
        for (let geom of geometry.geometries) {
            let reprojGeom = {};

            switch (GisService.inferValidGeoJsonGeomType(geom.type)) {
                case "MultiPoint":
                    reprojGeom = GisService.reprojectMultiPoint(
                        fromP,
                        toP,
                        geom
                    );
                    reprojGeoms.push(reprojGeom);
                    break;
                case "Point":
                    reprojGeom = GisService.reprojectPoint(fromP, toP, geom);
                    reprojGeoms.push(reprojGeom);
                    break;
                case "MultiPolygon":
                    reprojGeom = GisService.reprojectMultiPolygon(
                        fromP,
                        toP,
                        geom
                    );
                    reprojGeoms.push(reprojGeom);
                    break;
                case "Polygon":
                    reprojGeom = GisService.reprojectPolygon(fromP, toP, geom);
                    reprojGeoms.push(reprojGeom);
                    break;
                case "MultiLineString":
                    reprojGeom = GisService.reprojectMultiLineString(
                        fromP,
                        toP,
                        geom
                    );
                    reprojGeoms.push(reprojGeom);
                    break;
                case "LineString":
                    reprojGeom = GisService.reprojectLineString(
                        fromP,
                        toP,
                        geom
                    );
                    reprojGeoms.push(reprojGeom);
                    break;
                case "GeometryCollection":
                    if (geometry.geometries && geometry.geometries.length > 0) {
                        reprojGeom = GisService.reprojectGeometryCollection(
                            fromP,
                            toP,
                            geometry
                        );
                        reprojGeoms.push(reprojGeom);
                    }
                    break;
                default:
                    console.warn(
                        "reproj de geometrie de type " +
                            geom.type +
                            " non encore implementé"
                    );
                    break;
            }
        }

        return reprojGeoms;
    }

    public static reprojectFeature(
        fromP: string,
        toP: string,
        feature: Feature<any>
    ): any {
        let reprojGeom = {};
        let geomType = feature.geometry.type;

        switch (GisService.inferValidGeoJsonGeomType(geomType)) {
            case "MultiPoint":
                reprojGeom = GisService.reprojectMultiPoint(
                    fromP,
                    toP,
                    feature.geometry
                );
                break;
            case "Point":
                reprojGeom = GisService.reprojectPoint(
                    fromP,
                    toP,
                    feature.geometry
                );
                break;
            case "MultiPolygon":
                reprojGeom = GisService.reprojectMultiPolygon(
                    fromP,
                    toP,
                    feature.geometry
                );
                break;
            case "Polygon":
                reprojGeom = GisService.reprojectPolygon(
                    fromP,
                    toP,
                    feature.geometry
                );
                break;
            case "LineString":
                reprojGeom = GisService.reprojectLineString(
                    fromP,
                    toP,
                    feature.geometry
                );
                break;
            case "MultiLineString":
                reprojGeom = GisService.reprojectMultiLineString(
                    fromP,
                    toP,
                    feature.geometry
                );
                break;
            case "GeometryCollection":
                if (
                    feature.geometry.geometries &&
                    feature.geometry.geometries.length > 0
                ) {
                    let reprojGeoms = GisService.reprojectGeometryCollection(
                        fromP,
                        toP,
                        feature.geometry
                    );

                    let newFeature = {
                        type: "Feature",
                        geometry: {
                            type: GisService.inferValidGeoJsonGeomType(
                                geomType
                            ),
                            geometries: reprojGeoms
                        },
                        properties: feature.properties,
                        id: feature.id
                    };

                    return newFeature;
                }
                break;
            default:
                console.warn(
                    "reproj de geometrie de type " +
                        geomType +
                        " non encore implementé"
                );
                break;
        }

        // on arrive ici si ce n'est pas une geometrycollection
        let newFeature = {
            type: "Feature",
            geometry: reprojGeom,
            properties: feature.properties,
            id: feature.id
        };

        return newFeature;
    }

    public static reprojectPoint(
        fromP: string,
        toP: string,
        geometry: Point
    ): any {
        let array = [];
        let coord = geometry.coordinates;

        let reprojCoord = GisService.reproject(fromP, toP, {
            lat: coord[1],
            lng: coord[0]
        });
        array.push(reprojCoord.x, reprojCoord.y);

        let newGeometry = {
            type: GisService.inferValidGeoJsonGeomType(geometry.type),
            coordinates: array
        };

        return newGeometry;
    }

    public static reprojectMultiPoint(
        fromP: string,
        toP: string,
        geometry: MultiPoint
    ): any {
        let array = [];

        for (let coord of geometry.coordinates) {
            let reprojCoord = GisService.reproject(fromP, toP, {
                lat: coord[1],
                lng: coord[0]
            });
            array.push([reprojCoord.x, reprojCoord.y]);
        }

        let newGeometry = {
            type: GisService.inferValidGeoJsonGeomType(geometry.type),
            coordinates: array
        };

        return newGeometry;
    }

    public static reprojectPolygon(
        fromP: string,
        toP: string,
        geometry: Polygon
    ): any {
        let array = [];

        for (let shape of geometry.coordinates) {
            let reprojShape = [];
            for (let coord of shape) {
                let reprojCoord = GisService.reproject(fromP, toP, {
                    lat: coord[1],
                    lng: coord[0]
                });
                reprojShape.push([reprojCoord.x, reprojCoord.y]);
            }

            array.push(reprojShape);
        }

        let newGeometry = {
            type: GisService.inferValidGeoJsonGeomType(geometry.type),
            coordinates: array
        };

        return newGeometry;
    }

    public static reprojectMultiPolygon(
        fromP: string,
        toP: string,
        geometry: MultiPolygon
    ): any {
        let array = [];

        for (let polyg of geometry.coordinates) {
            let reprojPolyg = [];
            for (let shape of polyg) {
                let reprojShape = [];
                for (let coord of shape) {
                    let reprojCoord = GisService.reproject(fromP, toP, {
                        lat: coord[1],
                        lng: coord[0]
                    });
                    reprojShape.push([reprojCoord.x, reprojCoord.y]);
                }

                reprojPolyg.push(reprojShape);
            }
            array.push(reprojPolyg);
        }
        let newGeometry = {
            type: GisService.inferValidGeoJsonGeomType(geometry.type),
            coordinates: array
        };

        return newGeometry;
    }

    public static reprojectMultiLineString(
        fromP: string,
        toP: string,
        geometry: MultiLineString
    ): any {
        let array = [];

        for (let polyl of geometry.coordinates) {
            let reprojPolyl = [];
            for (let shape of polyl) {
                let reprojCoord = GisService.reproject(fromP, toP, {
                    lat: shape[1],
                    lng: shape[0]
                });
                reprojPolyl.push([reprojCoord.x, reprojCoord.y]);
            }
            array.push(reprojPolyl);
        }
        let newGeometry = {
            type: GisService.inferValidGeoJsonGeomType(geometry.type),
            coordinates: array
        };

        return newGeometry;
    }

    public static reprojectLineString(
        fromP: string,
        toP: string,
        geometry: LineString
    ): any {
        let array = [];

        for (let coord of geometry.coordinates) {
            let reprojCoord = GisService.reproject(fromP, toP, {
                lat: coord[1],
                lng: coord[0]
            });
            array.push([reprojCoord.x, reprojCoord.y]);
        }

        let newGeometry = {
            type: GisService.inferValidGeoJsonGeomType(geometry.type),
            coordinates: array
        };

        return newGeometry;
    }

    public static reprojectFeatureCollection(
        fromP: string,
        toP: string,
        geojson: FeatureCollection<any>
    ): any {
        let projectedFeatureCollection = {
            type: "FeatureCollection",
            features: []
        };

        // On boucle sur les features pour reprojeter en 4326
        for (let feature of geojson.features) {
            let newFeature = GisService.reprojectFeature(fromP, toP, feature);
            projectedFeatureCollection.features.push(newFeature);
        }

        return projectedFeatureCollection;
    }

    public static reproject(
        fromP: string = "EPSG:4326",
        toP: string = "EPSG:4326",
        coordinates: { lat; lng }
    ): any {
        let reprojCoords = proj4(
            GisService.PROJECTIONS[fromP].def,
            GisService.PROJECTIONS[toP].def,
            proj4.toPoint([coordinates.lng, coordinates.lat])
        );
        return reprojCoords;
    }

    public static inferValidGeoJsonGeomType(invalidType: string): string {
        switch (invalidType.toLowerCase()) {
            case "point":
                return "Point";
            case "multipoint":
                return "MultiPoint";
            case "linestring":
                return "LineString";
            case "multilinestring":
                return "MultiLineString";
            case "polygon":
                return "Polygon";
            case "multipolygon":
                return "MultiPolygon";
            case "geometrycollection":
                return "GeometryCollection";
        }
    }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/services/gis.service.ts